/* © 2017-2026 Booz Allen Hamilton Inc. All Rights Reserved. */

import React from 'react';
import PropTypes from 'prop-types';
import { SecurityHelper, PlaceholderText, Button, Stack } from 'sarsaparilla';
import get from 'lodash/get';
import { gnoment } from 'shared-ui/gnoment';

import LotteryWidget from '../lottery/LotteryWidget';
import QueuingLotteryWidget from '../lottery/QueuingLotteryWidget';

const FUTURE_LOTTERY_TITLE = 'Future Lottery';
const LOTTERY_OPEN_TITLE = 'Lottery Open';
const FUTURE_QUEUING_LOTTERY_TITLE = 'Future Lottery';
const QUEUING_LOTTERY_OPEN_TITLE = 'Early Access Lottery';
const AVAILABLE_PERMITS_TITLE = 'Available Permits';
const EARLY_ACCESS_PERIOD_TITLE = 'Early Access Period';

// Checks if account is commercial and has access to the permit
export function hasAccessToPermit(account, permitId) {
    if (account.is_commercial_account) {
        const availablePermit = get(account, ['special_use_permits', permitId]);
        return (
            availablePermit &&
            !availablePermit.is_suspended &&
            gnoment(availablePermit.expiration).isAfter(gnoment())
        );
    }
    return true;
}

function ImportantDatesButton({ handleImportantDates }) {
    return (
        <Button
            appearance="tertiary"
            shouldFitContainer
            onClick={handleImportantDates}
            screenReaderTextAfter="see important dates"
        >
            See Important Dates
        </Button>
    );
}

ImportantDatesButton.propTypes = {
    handleImportantDates: PropTypes.func,
};

function FutureLotteries({
    permit,
    futureLotteriesList,
    enterLottery,
    hasTitle,
    isOldLotteryFormat,
}) {
    return (
        <LotteryWidget
            futureLotteryTitle={hasTitle ? FUTURE_LOTTERY_TITLE : ''}
            isFutureLottery
            enterLottery={enterLottery}
            permit={permit}
            lotteries={futureLotteriesList}
            isOldLotteryFormat={isOldLotteryFormat}
        />
    );
}

FutureLotteries.propTypes = {
    permit: PropTypes.object,
    enterLottery: PropTypes.func,
    futureLotteriesList: PropTypes.array,
    hasTitle: PropTypes.bool,
    isOldLotteryFormat: PropTypes.bool,
};

function FutureQueueLotteries({
    permit,
    futureQueuingLotteries,
    enterLottery,
    hasTitle,
}) {
    return (
        <QueuingLotteryWidget
            futureLotteryTitle={hasTitle ? FUTURE_LOTTERY_TITLE : ''}
            isFutureLottery
            permit={permit}
            enterLottery={enterLottery}
            lotteries={futureQueuingLotteries}
        />
    );
}

FutureQueueLotteries.propTypes = {
    permit: PropTypes.object,
    enterLottery: PropTypes.func,
    futureQueuingLotteries: PropTypes.array,
    hasTitle: PropTypes.bool,
};

function getTitle(
    isClosed,
    openLotteries,
    futureLotteriesList,
    openQueuingLotteries,
    futureQueuingLotteries,
    earlyAccessPeriods
) {
    if (openLotteries.length) {
        return LOTTERY_OPEN_TITLE;
    }
    if (openQueuingLotteries.length) {
        return QUEUING_LOTTERY_OPEN_TITLE;
    }
    if (earlyAccessPeriods.length) {
        return EARLY_ACCESS_PERIOD_TITLE;
    }
    if (!isClosed) {
        // Open Season is higher priority than Future Lotteries
        return AVAILABLE_PERMITS_TITLE;
    }
    if (futureLotteriesList.length) {
        return FUTURE_LOTTERY_TITLE;
    }
    if (futureQueuingLotteries.length) {
        return FUTURE_QUEUING_LOTTERY_TITLE;
    }
    return AVAILABLE_PERMITS_TITLE;
}

export default function RightRailContainer({
    permit,
    isDisabledButton,
    isLoading,
    isLoadingButton,
    isClosed,
    openLotteries = [],
    futureLotteriesList = [],
    openQueuingLotteries = [],
    futureQueuingLotteries = [],
    handleEnterLottery,
    handleImportantDates,
    buttonList,
    children,
    extraChildren,
    earlyAccessPeriods = [],
    isOldLotteryFormat = false,
    desktopDisabled = false,
    earlyAccessPeriodWidget = null,
}) {
    const title = getTitle(
        isClosed,
        openLotteries,
        futureLotteriesList,
        openQueuingLotteries,
        futureQueuingLotteries,
        earlyAccessPeriods
    );
    const hasAnyLottery =
        openLotteries.length ||
        futureLotteriesList.length ||
        openQueuingLotteries.length ||
        futureQueuingLotteries.length ||
        earlyAccessPeriods.length;

    if (isLoading) {
        return {
            title: 'Loading...',
            body: (
                <div className="py-2 right-rail-card">
                    <PlaceholderText lines={2} fontSize={19} lineHeight={23} />
                    <PlaceholderText lines={3} fontSize={19} lineHeight={23} />
                </div>
            ),
        };
    }

    if (!hasAccessToPermit(SecurityHelper.getAccount(), permit.id)) {
        return {
            title: 'Not Authorized',
            notAuth: true,
            links: permit?.links,
            body: (
                <div className="py-2 right-rail-card">
                    <p>
                        You are not authorized to make commercial reservations at this
                        location, please contact the facility for more information.
                    </p>
                </div>
            ),
        };
    }

    if (isClosed && !hasAnyLottery) {
        const isMobileApp = SecurityHelper.isMobileApp();
        return {
            title: 'Registration Closed',
            links: permit?.links,
            body: (
                <div className="py-2 right-rail-card">
                    <p>Reservations have not been released yet.</p>
                    <p>
                        See the important dates section to view when registration opens.
                    </p>
                    {handleImportantDates && !isMobileApp && (
                        <ImportantDatesButton
                            handleImportantDates={handleImportantDates}
                        />
                    )}
                </div>
            ),
        };
    }

    const lotteryOpen = openLotteries?.length ? (
        <LotteryWidget
            key="LotteryWidget"
            permit={permit}
            lotteries={openLotteries}
            isDisabledButton={isDisabledButton}
            isLoadingButton={isLoadingButton}
            isOldLotteryFormat={isOldLotteryFormat}
            enterLottery={handleEnterLottery}
            desktopDisabled={desktopDisabled}
        />
    ) : null;

    const futureLotteries = futureLotteriesList?.length ? (
        <FutureLotteries
            key="FutureLotteryWidget"
            hasTitle={title !== FUTURE_LOTTERY_TITLE}
            permit={permit}
            futureLotteriesList={futureLotteriesList}
            isOldLotteryFormat={isOldLotteryFormat}
            enterLottery={handleEnterLottery}
        />
    ) : null;

    const openQueuingLottery = !!openQueuingLotteries?.length && (
        <QueuingLotteryWidget
            permit={permit}
            lotteries={openQueuingLotteries}
            onEnterLottery={handleEnterLottery}
            isLoadingButton={isLoadingButton}
        />
    );

    const futureQueuingLottery = !!futureQueuingLotteries?.length && (
        <FutureQueueLotteries
            key="FutureQueueLotteryWidget"
            hasTitle={title !== FUTURE_QUEUING_LOTTERY_TITLE}
            permit={permit}
            futureQueuingLotteries={futureQueuingLotteries}
            enterLottery={handleEnterLottery}
        />
    );

    const renderEarlyAccessPeriod = () => {
        const shouldRenderEAPPermitHeader = !!lotteryOpen || !!openQueuingLottery;

        return (
            !!earlyAccessPeriodWidget && (
                <div className="right-rail-card">
                    {shouldRenderEAPPermitHeader && (
                        <div className="rec-aside-subheader">
                            <div>{EARLY_ACCESS_PERIOD_TITLE}</div>
                        </div>
                    )}
                    {earlyAccessPeriodWidget}
                </div>
            )
        );
    };

    const renderAvailablePermit = () => {
        const shouldRenderAvailablePermitHeader =
            (!isClosed && !!lotteryOpen) ||
            !!openQueuingLottery ||
            !!earlyAccessPeriodWidget;

        return children?.length || buttonList?.length ? (
            <div className="right-rail-card">
                {shouldRenderAvailablePermitHeader && (
                    <div className="rec-aside-subheader">
                        <div>{AVAILABLE_PERMITS_TITLE}</div>
                    </div>
                )}
                {children}
                {buttonList}
            </div>
        ) : null;
    };

    const widgets = [
        lotteryOpen,
        openQueuingLottery,
        renderEarlyAccessPeriod(),
        renderAvailablePermit(),
        futureLotteries,
        futureQueuingLottery,
    ];

    return {
        title,
        links: permit?.links,
        body: (
            <Stack>
                {widgets}
                {extraChildren}
            </Stack>
        ),
    };
}

RightRailContainer.propTypes = {
    isDisabledButton: PropTypes.bool,
    isLoading: PropTypes.bool,
    isLoadingButton: PropTypes.bool,
    isClosed: PropTypes.bool,
    permit: PropTypes.object,
    openLotteries: PropTypes.array,
    futureLotteriesList: PropTypes.array,
    openQueuingLotteries: PropTypes.array,
    futureQueuingLotteries: PropTypes.array,
    earlyAccessPeriods: PropTypes.array,
    handleImportantDates: PropTypes.func,
    handleEnterLottery: PropTypes.func,
    children: PropTypes.array,
    extraChildren: PropTypes.array,
    buttonList: PropTypes.array,
    isOldLotteryFormat: PropTypes.bool,
    desktopDisabled: PropTypes.bool,
};
